import { useState } from 'react';
import type {
  BrandId,
  ModelPlanDetail,
  ModelPlanDryRunResponse,
  ModelPlanPropagationRunSummary,
  ModelPlanSkuInput,
  ModelPlanStatus,
  ModelPlanSummary,
  UpdateModelPlanRequest,
  UpdateModelPlanResponse,
} from '@nadoutong/contracts';
import { BRAND_IDS, normalizeBrandId } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  isModelPlanDryRunResponse,
  useArchiveModelPlan,
  useCloneModelPlan,
  useCreateModelPlan,
  useModelPlan,
  useModelPlanCountsIndex,
  useModelPlans,
  useModelPlanUnsyncedIndex,
  useUnarchiveModelPlan,
  useUpdateModelPlan,
} from '../api/modelPlans.ts';
import { BrandChip } from '../components/BrandChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import {
  MODEL_PLAN_MANAGE_ROLES,
  ROLE_OPS_ADMIN,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  THead,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';
import { PlanDetailDrawer, composeSyncUpdate } from './model-plans/PlanDetailDrawer.tsx';
import { PlanEditorDialog } from './model-plans/PlanEditorDialog.tsx';
import type { PlanWritePayload } from './model-plans/PlanEditorDialog.tsx';
import type { PlanFloorBreach, SkuResolvedRate } from './model-plans/floorBreaches.ts';
import {
  humanizeFloorBreaches,
  isModelPlanFloorError,
  modelPlanFloorBreachesFromError,
} from './model-plans/floorBreaches.ts';
import {
  BlastRadiusPreview,
  FloorBreachesList,
  planStatusTone,
  PropagationRunSummaryView,
} from './model-plans/preview.tsx';

/**
 * /market/model-plans — catalog templates bound to invite codes
 * (epic #2292 / issue #2297).
 *
 * One role-adaptive page:
 *   - customer manager / sales lead — brand-pinned read of plans;
 *   - admin — cross-brand with a brand filter, plus create / edit /
 *     clone / archive / rollback.
 *
 * The row scope is injected by ops-api. Action gating here is UI
 * affordance only.
 */

function isSalesProfileMissing(error: unknown): boolean {
  return error instanceof OpsApiError && error.code === 'SALES_PROFILE_NOT_FOUND';
}

type StatusFilter = ModelPlanStatus | 'all';

const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'active', 'archived'];

function statusFilterLabelKey(value: StatusFilter): I18nKey {
  if (value === 'active') return 'model_plans.filter.active';
  if (value === 'archived') return 'model_plans.filter.archived';
  return 'model_plans.filter.all';
}

function isStatusFilter(value: string): value is StatusFilter {
  return STATUS_FILTERS.some((candidate) => candidate === value);
}

type BrandFilter = BrandId | 'all';

const BRAND_FILTERS: readonly BrandFilter[] = ['all', ...BRAND_IDS];

function isBrandFilter(value: string): value is BrandFilter {
  return BRAND_FILTERS.some((candidate) => candidate === value);
}

export function ModelPlansPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user, status } = useSession();
  const roles = user?.roles ?? [];
  const isAdmin = hasAnyOpsRole(roles, [ROLE_OPS_ADMIN]);
  const canManage = hasAnyOpsRole(roles, MODEL_PLAN_MANAGE_ROLES);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [adminBrand, setAdminBrand] = useState<BrandFilter>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState<ModelPlanSummary | null>(null);
  const [pendingArchive, setPendingArchive] = useState<ModelPlanSummary | null>(null);
  const [pendingUnarchive, setPendingUnarchive] = useState<ModelPlanSummary | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<{
    readonly id: string;
    readonly body: UpdateModelPlanRequest;
    readonly preview: ModelPlanDryRunResponse;
    readonly resolvedSkus: readonly SkuResolvedRate[];
  } | null>(null);
  const [lastRun, setLastRun] = useState<ModelPlanPropagationRunSummary | null>(null);
  const [editorError, setEditorError] = useState<Error | null>(null);
  const [confirmFloorBreaches, setConfirmFloorBreaches] = useState<
    readonly PlanFloorBreach[] | null
  >(null);
  // The sync flow's race backstop: its dry-run 409 arrives with the editor
  // already closed and no dry-run dialog mounted, so the humanized breach
  // rows need their own surface.
  const [syncFloorBreaches, setSyncFloorBreaches] = useState<
    readonly PlanFloorBreach[] | null
  >(null);

  const query = useModelPlans(
    {
      status: statusFilter === 'all' ? undefined : statusFilter,
      brand: isAdmin && adminBrand !== 'all' ? adminBrand : undefined,
    },
    status === 'authenticated',
  );
  const create = useCreateModelPlan();
  const update = useUpdateModelPlan();
  const clone = useCloneModelPlan();
  const archive = useArchiveModelPlan();
  const unarchive = useUnarchiveModelPlan();
  const editing = useModelPlan(editingId, editingId !== null);

  const plans = query.data?.model_plans ?? [];
  const planIds = plans.map((plan) => plan.id);
  const counts = useModelPlanCountsIndex(planIds, query.isSuccess);
  const unsynced = useModelPlanUnsyncedIndex(planIds, query.isSuccess);

  function handleCreate(payload: PlanWritePayload): void {
    if (payload.create === null) return;
    setEditorError(null);
    create.mutate(payload.create, {
      onSuccess: () => {
        setCreateOpen(false);
        toast.success(t('model_plans.toast.created'));
      },
      onError: (error) => {
        setEditorError(error);
      },
    });
  }

  function handleEditPreview(payload: PlanWritePayload): void {
    if (editingId === null) return;
    setEditorError(null);
    update.mutate(
      { id: editingId, body: payload.update, dryRun: true },
      {
        onSuccess: (response) => {
          if (!isModelPlanDryRunResponse(response)) {
            setEditorError(new Error('unexpected dry-run response'));
            return;
          }
          setConfirmFloorBreaches(null);
          setDryRun({
            id: editingId,
            body: payload.update,
            preview: response,
            resolvedSkus: payload.resolvedSkus,
          });
        },
        onError: (error) => {
          setEditorError(error);
        },
      },
    );
  }

  function confirmEdit(): void {
    if (dryRun === null) return;
    update.mutate(
      { id: dryRun.id, body: dryRun.body, dryRun: false },
      {
        onSuccess: (response) => {
          if (isModelPlanDryRunResponse(response)) {
            toast.error(coerceErrorMessage(new Error('unexpected dry-run response')));
            return;
          }
          setDryRun(null);
          setConfirmFloorBreaches(null);
          setEditingId(null);
          if (response.propagation !== undefined) {
            setLastRun(response.propagation);
          }
          toast.success(t('model_plans.toast.updated'));
        },
        onError: (error) => {
          if (isModelPlanFloorError(error)) {
            const wire = modelPlanFloorBreachesFromError(error);
            if (wire !== null) {
              setConfirmFloorBreaches(humanizeFloorBreaches(wire, dryRun.resolvedSkus));
              return;
            }
            toast.error(t('model_plans.floor.unparsable'));
            return;
          }
          toast.error(coerceErrorMessage(error));
        },
      },
    );
  }

  function startSync(
    plan: ModelPlanDetail,
    admitted: readonly ModelPlanSkuInput[],
    resolvedSkus: readonly SkuResolvedRate[],
  ): void {
    const body = composeSyncUpdate(plan, admitted);
    setEditingId(null);
    setEditorError(null);
    update.mutate(
      { id: plan.id, body, dryRun: true },
      {
        onSuccess: (response) => {
          if (!isModelPlanDryRunResponse(response)) {
            toast.error(coerceErrorMessage(new Error('unexpected dry-run response')));
            return;
          }
          setConfirmFloorBreaches(null);
          setDryRun({ id: plan.id, body, preview: response, resolvedSkus });
        },
        onError: (error) => {
          if (isModelPlanFloorError(error)) {
            const wire = modelPlanFloorBreachesFromError(error);
            if (wire !== null) {
              setSyncFloorBreaches(humanizeFloorBreaches(wire, resolvedSkus));
              return;
            }
            toast.error(t('model_plans.floor.unparsable'));
            return;
          }
          toast.error(coerceErrorMessage(error));
        },
      },
    );
  }

  const dryRunFloorBreaches =
    dryRun === null
      ? []
      : confirmFloorBreaches ??
        humanizeFloorBreaches(dryRun.preview.floor_breaches, dryRun.resolvedSkus);
  const floorBlocked = dryRunFloorBreaches.length > 0;

  return (
    <section className="flex flex-col gap-8" data-testid="model-plans-page">
      <PageHeader
        title={t('model_plans.title')}
        sub={t('model_plans.sub')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <Select
                value={adminBrand}
                onValueChange={(next) => {
                  if (isBrandFilter(next)) setAdminBrand(next);
                }}
                options={BRAND_FILTERS.map((value) => ({
                  value,
                  label: value === 'all' ? t('model_plans.filter.brand_all') : value,
                }))}
                ariaLabel={t('model_plans.brand_aria')}
                mono
                testId="model-plans-brand-select"
              />
            ) : null}
            <Select
              value={statusFilter}
              onValueChange={(next) => {
                if (isStatusFilter(next)) setStatusFilter(next);
              }}
              options={STATUS_FILTERS.map((value) => ({
                value,
                label: t(statusFilterLabelKey(value)),
              }))}
              ariaLabel={t('model_plans.filter.aria')}
              testId="model-plans-status-filter"
            />
            {canManage ? (
              <Button
                variant="primary"
                onClick={() => {
                  setEditorError(null);
                  setCreateOpen(true);
                }}
                data-testid="model-plans-create-open"
              >
                {t('model_plans.action.create')}
              </Button>
            ) : (
              <Chip tone="neutral" testId="model-plans-readonly-chip">
                {t('model_plans.readonly')}
              </Chip>
            )}
          </div>
        }
      />

      {lastRun !== null ? (
        <PropagationRunSummaryView run={lastRun} testId="model-plans-last-run" />
      ) : null}

      {query.isLoading ? <TableSkeleton rows={4} /> : null}

      {query.isError && isSalesProfileMissing(query.error) ? (
        <EmptyState
          title={t('model_plans.no_profile.title')}
          hint={t('model_plans.no_profile.hint')}
          testId="model-plans-no-profile"
        />
      ) : null}

      {query.isError && !isSalesProfileMissing(query.error) ? (
        <EmptyState
          title={t('model_plans.error.load')}
          hint={coerceErrorMessage(query.error)}
          action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          testId="model-plans-error"
        />
      ) : null}

      {query.isSuccess ? (
        <Table testId="model-plans-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('model_plans.column.name')}</Th>
              <Th>{t('model_plans.column.brand')}</Th>
              <Th>{t('model_plans.column.status')}</Th>
              <Th>{t('model_plans.column.entries')}</Th>
              <Th num>{t('model_plans.column.bound_codes')}</Th>
              <Th num>{t('model_plans.column.assigned_orgs')}</Th>
              <Th>{t('model_plans.column.updated')}</Th>
              <Th>{t('model_plans.column.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {plans.length === 0 ? (
              <TableEmpty colSpan={8} testId="model-plans-empty">
                {t('model_plans.empty')}
              </TableEmpty>
            ) : null}
            {plans.map((plan) => {
              const count = counts.byPlanId?.get(plan.id);
              const unsyncedCount = unsynced.byPlanId?.get(plan.id) ?? 0;
              const brandId = normalizeBrandId(plan.brand);
              return (
                <Tr key={plan.id} data-testid={`model-plans-row-${plan.id}`}>
                  <Td emphasis>
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{plan.name}</span>
                      {unsyncedCount > 0 ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-auto px-0"
                          onClick={() => setDetailId(plan.id)}
                          data-testid={`model-plans-row-new-${plan.id}`}
                        >
                          <Chip tone="warn">
                            {t('model_plans.unsynced_badge', { count: unsyncedCount })}
                          </Chip>
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    {brandId === null ? (
                      <span className="font-mono">{plan.brand}</span>
                    ) : (
                      <BrandChip brand={brandId} testId={`model-plans-brand-${plan.id}`} />
                    )}
                  </Td>
                  <Td>
                    <Chip
                      tone={planStatusTone(plan.status)}
                      testId={`model-plans-status-${plan.id}`}
                    >
                      {t(
                        plan.status === 'active'
                          ? 'model_plans.status.active'
                          : 'model_plans.status.archived',
                      )}
                    </Chip>
                  </Td>
                  <Td>
                    {count === undefined ? '—' : `${count.entries} / ${count.skus}`}
                  </Td>
                  <Td num>{plan.bound_active_code_count}</Td>
                  <Td num>{plan.assigned_org_count}</Td>
                  <Td>{formatLocalTimestamp(plan.updated_at)}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setDetailId(plan.id)}
                        data-testid={`model-plans-detail-${plan.id}`}
                      >
                        {t('model_plans.action.detail')}
                      </Button>
                      {canManage ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCloning(plan)}
                          data-testid={`model-plans-clone-${plan.id}`}
                        >
                          {t('model_plans.action.clone')}
                        </Button>
                      ) : null}
                      {canManage && plan.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditorError(null);
                            setEditingId(plan.id);
                          }}
                          data-testid={`model-plans-edit-${plan.id}`}
                        >
                          {t('model_plans.action.edit')}
                        </Button>
                      ) : null}
                      {canManage && plan.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setPendingArchive(plan)}
                          data-testid={`model-plans-archive-${plan.id}`}
                        >
                          {t('model_plans.action.archive')}
                        </Button>
                      ) : null}
                      {canManage && plan.status === 'archived' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingUnarchive(plan)}
                          data-testid={`model-plans-unarchive-${plan.id}`}
                        >
                          {t('model_plans.action.unarchive')}
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      ) : null}

      {createOpen ? (
        <PlanEditorDialog
          mode="create"
          plan={null}
          onClose={() => {
            if (!create.isPending) setCreateOpen(false);
          }}
          onSubmit={handleCreate}
          busy={create.isPending}
          error={editorError}
          onDismissError={() => setEditorError(null)}
        />
      ) : null}

      {editingId !== null && editing.data !== undefined && dryRun === null ? (
        <PlanEditorDialog
          mode="edit"
          plan={editing.data.model_plan}
          onClose={() => {
            if (!update.isPending) setEditingId(null);
          }}
          onSubmit={handleEditPreview}
          busy={update.isPending}
          error={editorError}
          onDismissError={() => setEditorError(null)}
        />
      ) : null}

      {dryRun !== null ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !update.isPending) {
              setDryRun(null);
              setConfirmFloorBreaches(null);
            }
          }}
          title={t('model_plans.dry_run.title')}
          closeLabel={t('common.close')}
          size="lg"
          testId="model-plans-dry-run-dialog"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setDryRun(null)}
                disabled={update.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={confirmEdit}
                loading={update.isPending}
                disabled={floorBlocked}
                data-testid="model-plans-dry-run-confirm"
              >
                {t('model_plans.dry_run.confirm')}
              </Button>
            </>
          }
        >
          <BlastRadiusPreview
            preview={dryRun.preview}
            floorBreaches={dryRunFloorBreaches}
            testId="model-plans-dry-run"
          />
          {floorBlocked ? (
            <p className="mt-3 m-0 text-dense text-warn" role="alert">
              {t('model_plans.dry_run.blocked')}
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {syncFloorBreaches !== null ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setSyncFloorBreaches(null);
          }}
          title={t('model_plans.floor.block_title')}
          closeLabel={t('common.close')}
          size="lg"
          testId="model-plans-sync-floor-dialog"
          footer={
            <Button
              variant="primary"
              onClick={() => setSyncFloorBreaches(null)}
              data-testid="model-plans-sync-floor-back"
            >
              {t('model_plans.floor.block_back')}
            </Button>
          }
        >
          <FloorBreachesList
            breaches={syncFloorBreaches}
            testId="model-plans-sync-floor-breaches"
          />
        </Dialog>
      ) : null}

      {cloning !== null ? (
        <CloneDialog
          plan={cloning}
          busy={clone.isPending}
          onClose={() => {
            if (!clone.isPending) setCloning(null);
          }}
          onSubmit={(name) => {
            clone.mutate(
              { id: cloning.id, body: { name } },
              {
                onSuccess: (response) => {
                  setCloning(null);
                  toast.success(
                    t('model_plans.toast.cloned', { name: response.model_plan.name }),
                  );
                },
                onError: (error) => {
                  toast.error(coerceErrorMessage(error));
                },
              },
            );
          }}
        />
      ) : null}

      {detailId !== null ? (
        <PlanDetailDrawer
          planId={detailId}
          onClose={() => setDetailId(null)}
          onSync={startSync}
        />
      ) : null}

      <ConfirmDialog
        open={pendingArchive !== null}
        onOpenChange={(open) => {
          if (!open && !archive.isPending) setPendingArchive(null);
        }}
        title={t('model_plans.archive.title')}
        body={t('model_plans.archive.body')}
        confirmLabel={t('model_plans.action.archive')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        loading={archive.isPending}
        onConfirm={() => {
          if (pendingArchive === null) return;
          archive.mutate(pendingArchive.id, {
            onSuccess: () => {
              setPendingArchive(null);
              toast.success(t('model_plans.toast.archived'));
            },
            onError: (error) => {
              if (error instanceof OpsApiError && error.code === 'MODEL_PLAN_HAS_ACTIVE_CODES') {
                toast.error(t('model_plans.archive.active_codes'));
                return;
              }
              toast.error(coerceErrorMessage(error));
            },
          });
        }}
        testId="model-plans-archive-confirm"
      />

      <ConfirmDialog
        open={pendingUnarchive !== null}
        onOpenChange={(open) => {
          if (!open && !unarchive.isPending) setPendingUnarchive(null);
        }}
        title={t('model_plans.unarchive.title')}
        body={t('model_plans.unarchive.body', { name: pendingUnarchive?.name ?? '' })}
        confirmLabel={t('model_plans.action.unarchive')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={unarchive.isPending}
        onConfirm={() => {
          if (pendingUnarchive === null) return;
          unarchive.mutate(pendingUnarchive.id, {
            onSuccess: () => {
              setPendingUnarchive(null);
              toast.success(t('model_plans.toast.unarchived'));
            },
            onError: (error) => {
              toast.error(coerceErrorMessage(error));
            },
          });
        }}
        testId="model-plans-unarchive-confirm"
      />
    </section>
  );
}

function CloneDialog({
  plan,
  busy,
  onClose,
  onSubmit,
}: {
  readonly plan: ModelPlanSummary;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (name: string) => void;
}): JSX.Element {
  const t = useT();
  const [name, setName] = useState(`${plan.name} copy`);
  const [validationError, setValidationError] = useState<string | null>(null);

  function submit(): void {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setValidationError(t('model_plans.clone.error.name'));
      return;
    }
    setValidationError(null);
    onSubmit(trimmed);
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title={t('model_plans.clone.title')}
      description={t('model_plans.clone.body')}
      closeLabel={t('common.close')}
      testId="model-plans-clone-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={busy}
            data-testid="model-plans-clone-submit"
          >
            {t('model_plans.clone.submit')}
          </Button>
        </>
      }
    >
      <FormField label={t('model_plans.clone.name')} fieldId="model-plans-clone-name">
        <Input
          id="model-plans-clone-name"
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          data-testid="model-plans-clone-name"
        />
      </FormField>
      {validationError !== null ? (
        <p role="alert" className="mt-3 m-0 text-dense text-bad">
          {validationError}
        </p>
      ) : null}
    </Dialog>
  );
}
